import { CreditCard, Calendar, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  superAdminBillingService,
  type BillingPaymentMethod,
} from "@/services/superAdminBillingService";

type Props = {
  paymentMethods: BillingPaymentMethod[];
  loading?: boolean;
};

export default function PaymentMethods({ paymentMethods, loading }: Props) {
  const getCardBrandIcon = (brand: string) => {
    const brandColors: Record<string, string> = {
      visa: "text-blue-600",
      mastercard: "text-red-600",
      amex: "text-blue-500",
      discover: "text-orange-600",
      diners: "text-gray-600",
      jcb: "text-green-600",
      unionpay: "text-red-500",
    };
    return brandColors[brand?.toLowerCase()] || "text-gray-600";
  };

  const formatExpiry = (month: number, year: number) => {
    return `${month.toString().padStart(2, "0")}/${year.toString().slice(-2)}`;
  };

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[1, 2].map((i) => (
          <div key={i} className="h-32 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (paymentMethods.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 border rounded-lg">
        No payment methods on file
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {paymentMethods.map((method, index) => (
        <Card key={method.id} className="relative overflow-hidden">
          {index === 0 && (
            <div className="absolute top-2 right-2">
              <Badge variant="secondary" className="text-xs">
                Default
              </Badge>
            </div>
          )}
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div
                className={`p-2 rounded-lg bg-gray-50 ${getCardBrandIcon(method.card?.brand || "")}`}
              >
                <CreditCard className="h-5 w-5" />
              </div>
              <div className="flex-grow">
                <div className="font-medium text-sm capitalize">
                  {method.card?.brand || method.type}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  •••• {method.card?.last4}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                  {method.card && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatExpiry(
                        method.card.exp_month,
                        method.card.exp_year,
                      )}
                    </div>
                  )}
                  {method.billing_details?.name && (
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {method.billing_details.name}
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  Added {superAdminBillingService.formatDate(method.created)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
